import { ulid } from 'ulid';
import {
  ApiError,
  completeFile,
  createFile,
  getFile,
  redeliverUploads,
  type Entity,
  type Upload,
} from './api.js';

/**
 * L'envoi d'octets depuis le navigateur — **le premier du projet**.
 *
 * Le protocole est celui de `api.md` §3, et chacun de ses trois temps existe pour
 * une raison qu'on ne voit pas en le lisant :
 *
 * ① `POST /files` crée l'entité **et** rend les URL présignées. L'entité existe
 *    donc avant ses octets, en `PENDING_UPLOAD`, et sur l'horloge des 30 jours
 *    (D19) : un envoi abandonné s'efface tout seul, sans balayage.
 * ② Le `PUT` va **directement à S3**, jamais à travers l'API : Lambda n'a ni la
 *    mémoire ni le temps d'un fichier de deux gigaoctets.
 * ③ `POST /complete` fait **constater** les octets par un `HeadObject`. C'est le
 *    serveur qui décide qu'ils existent, jamais le client qui l'affirme — sans
 *    quoi une entité annoncerait des octets absents et rendrait des URL vers rien.
 *
 * **Et un quatrième temps, qui ne se voit que quand ça casse : la reprise.** Une
 * URL présignée meurt en 900 s, l'entité vit 30 jours — l'écart est délibéré, et
 * `POST /files/{fid}/uploads` en est le pont (D19). Sans lui, un `PUT` en erreur
 * laissait ici une entité `PENDING_UPLOAD` que **rien** ne terminait : elle vivait
 * trente jours puis disparaissait, sans que personne ait jamais su qu'il y avait
 * eu un envoi. Le mobile portait le même défaut, corrigé en session B ; c'est
 * cette forme-là qui est reprise.
 */

/** Ce que l'appelant apprend pendant que les octets partent. */
export type ProgressFn = (sent: number, total: number) => void;

/**
 * Les en-têtes signés se renvoient **tels quels** (I-21), sans en ajouter aucun.
 *
 * ### `XMLHttpRequest` et non `fetch`, et c'est la seule raison *(2026-09-12)*
 *
 * `fetch` **ne sait pas** rapporter la progression d'un corps de requête dans un
 * navigateur : il n'expose que la progression de la *réponse*. Le retour d'usage
 * décrit exactement le symptôme — « on ne voit pas une barre de progression des
 * octets uploadés, en particulier sur les gros fichiers ou en cas de débit faible
 * on attend sans rien voir bouger ». Sur un `PUT` de 2 Gio vers S3, l'écran n'avait
 * littéralement rien à dire entre le début et la fin.
 *
 * `xhr.upload.onprogress` le donne, et c'est la seule capacité qui motive ce
 * retour en arrière apparent. Tout le reste est conservé :
 *
 * - **les en-têtes signés partent tels quels** — aucun des quatre que le serveur
 *   signe (`content-type`, `content-disposition`, `x-amz-tagging`,
 *   `x-amz-storage-class`) ne figure dans la liste des en-têtes interdits, donc
 *   `setRequestHeader` les accepte comme `fetch` les acceptait ;
 * - **le corps de l'erreur S3 remonte en clair**, parce que c'est lui qui distingue
 *   un `SignatureDoesNotMatch` d'un `AccessDenied` de politique ;
 * - **un échec réseau rejette**, ce dont dépend toute la reprise de `uploadFile`.
 */
export function putBytes(upload: Upload, file: Blob, onProgress?: ProgressFn): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', upload.url, true);
    for (const [name, value] of Object.entries(upload.headers)) {
      xhr.setRequestHeader(name, value);
    }

    // `lengthComputable` est faux sur un flux de taille inconnue ; ici le corps est
    // un `Blob`, donc il est toujours vrai — mais l'annoncer quand même évite de
    // publier un `total` de zéro, qui ferait une barre à l'infini.
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total);
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // **La barre se termine sur la réponse, pas sur le dernier événement.** Le
        // dernier `progress` peut manquer, et une barre figée à 98 % après un envoi
        // réussi se lit comme un blocage.
        onProgress?.(file.size, file.size);
        resolve();
        return;
      }
      reject(new Error(`envoi refusé par S3 (${xhr.status}) : ${xhr.responseText}`));
    };

    // **Trois causes, une seule issue.** `onerror` couvre la coupure et le refus
    // CORS, `ontimeout` l'absence de réponse, `onabort` la navigation : dans les
    // trois cas la seule reprise possible est une URL neuve, et `uploadFile` ne
    // distingue déjà pas la panne de réseau du refus de S3.
    xhr.onerror = () => reject(new Error('envoi interrompu (réseau)'));
    xhr.ontimeout = () => reject(new Error('envoi interrompu (délai dépassé)'));
    xhr.onabort = () => reject(new Error('envoi interrompu'));

    xhr.send(file);
  });
}

/** `IMG`, `VID`, ou rien — un document n'a pas de sous-nature. */
export function classify(file: File): { type: string; kind?: string } {
  if (file.type.startsWith('image/')) return { type: 'MEDIA', kind: 'IMG' };
  if (file.type.startsWith('video/')) return { type: 'MEDIA', kind: 'VID' };
  return { type: 'DOC' };
}

const extOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : 'bin';
};

/**
 * Les temps du protocole, nommés — parce qu'une barre d'attente muette pendant
 * quarante secondes se lit comme un blocage.
 *
 * `resuming` n'en est pas un quatrième : c'est un `sending` qui recommence sur
 * une URL neuve. Il est nommé parce qu'un envoi qui reprend **du début** après
 * cinq minutes, sans le dire, se lit comme une page bloquée.
 */
export type UploadPhase = 'creating' | 'sending' | 'resuming' | 'completing';

/**
 * Les quatre appels que l'envoi passe, **injectables**.
 *
 * Ce n'est pas de l'abstraction pour l'abstraction : c'est le seul moyen
 * d'éprouver la reprise **sans compte ni site déployé**. Les trois chemins qui
 * comptent — signature expirée, `missing_bytes`, deux échecs d'affilée — ne se
 * provoquent pas contre une pile réelle : il faudrait attendre 900 s, ou
 * fabriquer un S3 qui acquitte sans écrire.
 */
export interface UploadPorts {
  readonly create: typeof createFile;
  readonly redeliver: typeof redeliverUploads;
  readonly put: (upload: Upload, file: Blob, onProgress?: ProgressFn) => Promise<void>;
  readonly complete: typeof completeFile;
}

const LIVE: UploadPorts = {
  create: createFile,
  redeliver: redeliverUploads,
  put: putBytes,
  complete: completeFile,
};

export interface UploadOptions {
  readonly onPhase?: (phase: UploadPhase) => void;
  /**
   * **Les octets partis, au fil de l'eau.** Une reprise repart de zéro et le dit
   * par `onPhase('resuming')` : la barre recule donc, ce qui est la vérité —
   * masquer le recul ferait croire à une progression qui n'a pas lieu.
   */
  readonly onProgress?: ProgressFn;
  readonly ports?: UploadPorts;
  /** L'empreinte déjà calculée par l'import *(K2)*, portée par la création. */
  readonly sha256?: string;
}

/**
 * L'URL d'envoi de `source`, redélivrée. Une entité `PROCESSING` ou `READY`
 * répond `409 already_complete` : la reprise n'a alors plus de sens, et l'erreur
 * remonte telle quelle plutôt que d'être traduite en « échec d'envoi », qui
 * ferait chercher au mauvais endroit.
 */
async function freshUpload(ports: UploadPorts, fid: string): Promise<Upload> {
  const { uploads } = await ports.redeliver(fid, ['source']);
  const upload = uploads['source'];
  if (!upload) throw new Error('aucune URL d’envoi redélivrée pour le rôle source');
  return upload;
}

/**
 * Envoie un fichier de bout en bout et rend l'entité **constatée**.
 *
 * `folder` est **facultatif, pour tous les types** depuis 4.5. Absent, le serveur
 * pose le dossier par défaut du type — `/Photos`, `/Documents`, `/Notes`. C'est
 * la nuance qui compte : envoyer la racine « parce qu'on n'est pas dans un
 * dossier » **écraserait** ce défaut, et une photo envoyée depuis « Mes
 * fichiers » atterrirait à la racine plutôt que dans `/Photos`. Ne rien dire et
 * laisser le serveur trancher n'est pas la même chose que dire « racine ».
 */
export async function uploadFile(
  file: File,
  folder?: string,
  opts: UploadOptions = {},
): Promise<Entity> {
  const ports = opts.ports ?? LIVE;
  const onPhase = opts.onPhase;
  const { type, kind } = classify(file);
  const fid = ulid();

  onPhase?.('creating');
  const created = await ports.create({
    fid,
    type,
    kind,
    name: file.name,
    ...(folder ? { folder } : {}),
    ...(opts.sha256 ? { sha256: opts.sha256 } : {}),
    roles: [
      {
        role: 'source',
        ext: extOf(file.name),
        // Un type MIME vide arrive pour de vrai — extension inconnue du système.
        // Le serveur a sa propre règle de résolution ; on ne lui ment pas.
        mime: file.type || 'application/octet-stream',
      },
    ],
  });

  const upload = created.uploads?.['source'];
  if (!upload) throw new Error('aucune URL d’envoi rendue pour le rôle source');

  onPhase?.('sending');
  try {
    await ports.put(upload, file, opts.onProgress);
  } catch {
    // On ne distingue pas la panne de réseau du refus de S3 : dans les deux cas
    // la seule reprise possible est une URL neuve, et la demander coûte un
    // aller-retour contre les mégaoctets qu'on s'apprête à renvoyer.
    //
    // **Une seule fois** : au second échec, c'est une panne, pas une expiration —
    // une signature refabriquée à l'identique (`SignatureDoesNotMatch`, I-21) le
    // resterait, et insister ferait une boucle contre un serveur qui a déjà
    // répondu.
    onPhase?.('resuming');
    await ports.put(await freshUpload(ports, fid), file, opts.onProgress);
  }

  onPhase?.('completing');
  try {
    return await ports.complete(fid, ['source']);
  } catch (err) {
    // `409 missing_bytes` : S3 a acquitté un `PUT` dont l'objet n'existe pas — ce
    // que le serveur constate au lieu de le croire (I-16). **Aucune écriture n'a
    // eu lieu** : l'entité est restée `PENDING_UPLOAD` avec son `Ttl`, et renvoyer
    // les octets est la reprise que documente `api.md` §3.
    if (!(err instanceof ApiError) || err.code !== 'missing_bytes') throw err;
    onPhase?.('resuming');
    await ports.put(await freshUpload(ports, fid), file, opts.onProgress);
    onPhase?.('completing');
    return ports.complete(fid, ['source']);
  }
}

/**
 * Attend que le worker ait produit les dérivés.
 *
 * `PROCESSING` n'est pas une panne et `FAILED` n'est pas une exception : ce sont
 * deux issues du même job, et l'appelant doit pouvoir montrer la seconde plutôt
 * que de tourner indéfiniment. Le délai est généreux — une vidéo transcode.
 */
export async function waitReady(
  fid: string,
  opts: { timeoutMs?: number; everyMs?: number } = {},
): Promise<Entity> {
  const deadline = Date.now() + (opts.timeoutMs ?? 90_000);
  const every = opts.everyMs ?? 2_000;

  for (;;) {
    const entity = await getFile(fid);
    if (entity.State === 'READY' || entity.State === 'FAILED') return entity;
    if (Date.now() > deadline) return entity;
    await new Promise((r) => setTimeout(r, every));
  }
}
